import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';
import { ActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { useModalStore } from '@/Domain/store/modal.store';

const shopifyMcpSchema = (isActive: boolean) => {
  if (isActive) {
    return z.object({
      shopify_url: z.string().url().min(1),
    });
  }
  return z.object({
    shopify_url: z.string(),
  });
};

export type ShopifyMcpForm = z.infer<typeof shopifyMcpSchema>;

export const useShopifyMcpActionForm = (shopifyMcpAction?: ShopifyMcpDto) => {
  const { t } = useTranslation('shopify_mcp');
  const queryClient = useQueryClient();
  const { open, closeModal } = useModalStore();

  useEffect(() => {
    if (!shopifyMcpAction) return;
  }, [shopifyMcpAction]);

  const mutation = useMutation<ShopifyMcpDto, unknown, Partial<ShopifyMcpDto>>({
    mutationKey: [QueryKey.actions.shopifyMcp.update, shopifyMcpAction?.id],
    onSuccess: (data) => {
      closeModal();

      queryClient.setQueryData([QueryKey.actions.shopifyMcp.get, shopifyMcpAction?.id], () => ({
        ...data,
      }));

      void queryClient.invalidateQueries({ queryKey: [QueryKey.actions.basic.list] });
      toast.success(t('toast.update_success'));
    },
    onError: () => {
      toast.error(t('toast.update_failed'), {
        duration: 5000,
        description: t('toast.update_failed'),
      });
    },
  });

  const form = useForm<ShopifyMcpForm>({
    mode: 'onChange',
    defaultValues: {
      shopify_url: shopifyMcpAction?.shopify_url || '',
    },
    resolver: zodResolver(shopifyMcpSchema(!!shopifyMcpAction?.is_active)),
  });

  const createShopifyMCPActionMutation = useMutation<ShopifyMcpDto, unknown, void>({
    mutationKey: [QueryKey.actions.shopifyMcp.create],
  });

  const isSubmittingRef = useRef(false);

  const handleSubmit = async (data: ShopifyMcpForm) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      let shopifyMcpActionId = shopifyMcpAction?.id;
      if (!shopifyMcpActionId) {
        // Only one Shopify MCP action is allowed per scenario; Shopify-install agents already have one auto-provisioned, so reuse it instead of creating a duplicate (which 400s).
        let actions: ActionDto[] | undefined;
        try {
          actions = await queryClient.ensureQueryData<ActionDto[]>({
            queryKey: [QueryKey.actions.basic.list],
          });
        } catch {
          toast.error(t('toast.create_failed'));
          return;
        }
        const existing = actions?.find((action) => action.type === ActionTypeEnum.ShopifyRemoteMcp);
        shopifyMcpActionId =
          existing?.id ?? (await createShopifyMCPActionMutation.mutateAsync()).id;
      }
      mutation.mutate({
        id: shopifyMcpActionId,
        shopify_url: data.shopify_url,
        shopify_mcp_url: data.shopify_url ? `${data.shopify_url}/api/mcp` : data.shopify_url,
      });
    } catch {
      // Create failures surface via the create mutation's onError toast; swallow so they don't bubble as an uncaught rejection.
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    if (shopifyMcpAction) {
      form.reset({
        shopify_url: shopifyMcpAction.shopify_url,
      });

      return;
    }

    form.reset({
      shopify_url: '',
    });
  }, [open, shopifyMcpAction]);

  const onInvalid = () => {
    toast.error(t('toast.invalid_update_on_is_active'));
  };

  return { form, handleSubmit, onInvalid };
};
